import { useCallback, useState } from "react";
import { trademarkApi } from "../api/trademarkApi";
import { prepareLogoForOcr } from "../lib/logoImage";

/**
 * "Search by logo", shared by every page that has a SearchConsole.
 *
 * A logo search is a two-step job: read the words out of the image (OCR on
 * the server), then run the page's own ordinary name search with that text.
 * The second step belongs to the page — the main search stores its result
 * and navigates to /results, Potential licensing keeps it local — so the
 * page passes its runner in and this hook only owns the OCR step and the
 * messages around it.
 *
 *   const logo = useLogoSearch();
 *   <SearchConsole
 *     onLogoSearch={(args) => logo.searchByLogo(args, myRunSearch)}
 *     loading={searching || logo.reading}
 *   />
 *   {logo.error && <Alert tone="error">{logo.error}</Alert>}
 *   {logo.notice && <Alert tone="info">{logo.notice}</Alert>}
 */
export default function useLogoSearch() {
  const [reading, setReading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const clear = useCallback(() => {
    setNotice(null);
    setError(null);
  }, []);

  /**
   * @param {{ file: File, options?: Object }} args from SearchConsole
   * @param {(payload: Object) => Promise<any>} runSearch the page's name search
   */
  const searchByLogo = useCallback(async ({ file, options }, runSearch) => {
    setNotice(null);
    setError(null);

    let text;
    try {
      setReading(true);
      const prepared = await prepareLogoForOcr(file);
      const result = await trademarkApi.extractLogoText(prepared);
      text = result.text;
    } catch (err) {
      setError(err.message);
      return;
    } finally {
      setReading(false);
    }

    setNotice(
      `Read “${text}” from your logo — searching the registers for it.`,
    );
    await runSearch({ ...options, wordMark: text });
  }, []);

  return { reading, notice, error, searchByLogo, clear };
}
